import { useEffect, useState, useRef, useCallback, type ComponentType } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Loader2, Link2, MessageSquare, Bell, AlertTriangle, MessageSquareWarning } from "lucide-react";
import { useMessages, type UserMessage, type MessageThread } from "@/shared/hooks/useMessages";
import { useAuth } from "@/shared/hooks/useAuth";
import { useToast } from "@/shared/hooks/use-toast";
import { cn } from "@/shared/lib/utils";
import { supabase } from "@/shared/integrations/supabase/client";
import { fetchLabelProcessoVinculo, hrefProcessoVinculo, labelTipoVinculo } from "./correioVinculoHelpers";

const typeIcons: Record<string, ComponentType<{ className?: string }>> = {
  direct: MessageSquare,
  system: Bell,
  feedback_response: AlertTriangle,
  feedback_recebido: MessageSquareWarning,
};

export interface ResumoGeradoParaModal {
  resumoId: string;
  titulo: string;
  resumo: string;
  decisoes: string | null;
  proximosPassos: string | null;
  modelo: string;
  vinculoTipo: string;
  vinculoId: string;
  threadId: string;
  threadAssunto: string;
}

export interface ThreadViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadId: string;
  /** Chamado após fluxo de conclusão bem-sucedido (ex.: refetch correio). */
  onConcluir?: () => void;
  /** Quando a IA retorna o rascunho — abre o ResumoIAModal no componente pai. */
  onResumoGerado?: (payload: ResumoGeradoParaModal) => void;
}

function iniciais(nome: string): string {
  const p = nome.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return "?";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function statusLabel(status: string): string {
  if (status === "aberta") return "Aberta";
  if (status === "concluida") return "Concluída";
  if (status === "resumida") return "Resumida";
  return status;
}

const ThreadView = ({ open, onOpenChange, threadId, onConcluir, onResumoGerado }: ThreadViewProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { fetchThread, fetchThreadMessages, concluirThread, gerarResumoIA } = useMessages();

  const [thread, setThread] = useState<MessageThread | null>(null);
  const [msgs, setMsgs] = useState<UserMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [vinculoLabel, setVinculoLabel] = useState<string | null>(null);
  const [concluindo, setConcluindo] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const { data: myProfileId } = useQuery({
    queryKey: ["thread-view-my-profile", user?.id],
    enabled: !!user?.id,
    staleTime: 300_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data?.id ?? null;
    },
  });

  const load = useCallback(async () => {
    if (!threadId) return;
    setLoading(true);
    try {
      const [t, list] = await Promise.all([fetchThread(threadId), fetchThreadMessages(threadId)]);
      setThread(t);
      setMsgs(list);
      if (t?.vinculo_tipo && t?.vinculo_id) {
        const lbl = await fetchLabelProcessoVinculo(t.vinculo_tipo, t.vinculo_id);
        setVinculoLabel(lbl);
      } else {
        setVinculoLabel(null);
      }
    } finally {
      setLoading(false);
    }
  }, [threadId, fetchThread, fetchThreadMessages]);

  useEffect(() => {
    if (!open || !threadId) return;
    void load();
  }, [open, threadId, load]);

  useEffect(() => {
    if (!open || loading || msgs.length === 0) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [open, loading, msgs.length, msgs]);

  const vinculoHref =
    thread?.vinculo_tipo && thread?.vinculo_id
      ? hrefProcessoVinculo(thread.vinculo_tipo, thread.vinculo_id)
      : null;

  const podeConcluir =
    !!user &&
    !!myProfileId &&
    thread?.status === "aberta" &&
    thread.created_by === myProfileId &&
    !!thread.vinculo_tipo &&
    !!thread.vinculo_id;

  const handleConcluir = async () => {
    if (!thread || !threadId) return;
    setConcluindo(true);
    try {
      const ok = await concluirThread(threadId);
      if (!ok) return;
      const resumo = await gerarResumoIA(threadId);
      if (!resumo) return;
      const payload: ResumoGeradoParaModal = {
        resumoId: resumo.resumo_id,
        titulo: resumo.titulo,
        resumo: resumo.resumo,
        decisoes: resumo.decisoes ?? null,
        proximosPassos: resumo.proximos_passos ?? null,
        modelo: resumo.modelo,
        vinculoTipo: thread.vinculo_tipo || "",
        vinculoId: thread.vinculo_id || "",
        threadId,
        threadAssunto: thread.assunto,
      };
      onResumoGerado?.(payload);
      onConcluir?.();
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Erro ao concluir",
        description: e instanceof Error ? e.message : "Tente novamente",
        variant: "destructive",
      });
    } finally {
      setConcluindo(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex flex-col gap-0 p-0 overflow-hidden max-w-[700px] w-[calc(100%-1.5rem)]",
          "h-[min(85dvh,750px)] max-h-[90dvh] border-[rgba(169,171,174,0.35)] bg-[#f9f6f0] rounded-xl"
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle className="font-display">Thread de mensagens</DialogTitle>
          <DialogDescription>Conversa completa do correio interno</DialogDescription>
        </DialogHeader>

        <div className="shrink-0 bg-[#791614] text-[#f9f6f0] px-4 py-3 flex flex-col gap-2 rounded-t-xl">
          <div className="flex items-start justify-between gap-2 min-w-0">
            <h2 className="font-display text-lg font-semibold text-[#d7b369] leading-snug break-words flex-1 min-w-0">
              {thread?.assunto || "Thread"}
            </h2>
            {thread && (
              <Badge
                variant="secondary"
                className="shrink-0 bg-[#f9f6f0]/15 text-[#f9f6f0] border border-[#d7b369]/40 font-accent text-[9px] uppercase tracking-widest"
              >
                {statusLabel(thread.status)}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[#f9f6f0]/85">
            <span className="tabular-nums">{msgs.length} mensagem(ns)</span>
            {thread && thread.status !== "aberta" && (
              <span className="text-[#d7b369]/90 font-accent uppercase tracking-wide text-[10px]">
                {thread.status === "concluida" ? "Aguardando ou em revisão de resumo" : "Andamento pode estar registrado"}
              </span>
            )}
          </div>
        </div>

        {thread?.vinculo_tipo && thread.vinculo_id && (
          <div className="shrink-0 px-3 py-2 border-b border-[rgba(169,171,174,0.35)] bg-[#f9f6f0]">
            <div className="rounded-lg border border-[rgba(215,179,105,0.45)] bg-[#f9f6f0] px-3 py-2 shadow-sm">
              <p className="font-accent text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
                Vínculo · {labelTipoVinculo(thread.vinculo_tipo)}
              </p>
              <div className="flex items-center gap-2 min-w-0">
                <Link2 className="h-4 w-4 shrink-0 text-[#791614]" />
                {vinculoHref ? (
                  <Link
                    to={vinculoHref}
                    className="text-sm font-medium text-[#791614] hover:underline truncate"
                    onClick={() => onOpenChange(false)}
                  >
                    {vinculoLabel || thread.vinculo_id.slice(0, 8) + "…"}
                  </Link>
                ) : (
                  <span className="text-sm text-foreground truncate">
                    {vinculoLabel || `${thread.vinculo_tipo} · ${thread.vinculo_id.slice(0, 8)}…`}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <ScrollArea className="flex-1 min-h-0 bg-[#f9f6f0]">
          <div className="p-3 pb-24 space-y-3">
            {thread?.status === "aberta" &&
              user &&
              myProfileId &&
              thread.created_by === myProfileId &&
              (!thread.vinculo_tipo || !thread.vinculo_id) && (
                <div className="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2 text-xs text-amber-950">
                  Para concluir a thread e gerar resumo com registro de andamento, a conversa precisa estar vinculada a um processo (use responder/nova mensagem com vínculo).
                </div>
              )}
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-[#791614]" />
              </div>
            ) : msgs.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-12">Nenhuma mensagem nesta thread.</p>
            ) : (
              msgs.map((m) => {
                const isSystem = m.message_type === "system";
                const isOwn = !!user && m.sender_id === user.id;
                const Icon = typeIcons[m.message_type] ?? MessageSquare;

                if (isSystem) {
                  return (
                    <div key={m.id} className="flex justify-center px-2">
                      <div className="max-w-[92%] rounded-lg border border-[rgba(169,171,174,0.4)] bg-[#a9abae]/15 px-3 py-2 text-xs text-muted-foreground text-center whitespace-pre-wrap break-words">
                        <div className="flex items-center justify-center gap-1 mb-1 text-[10px] font-accent uppercase tracking-widest text-muted-foreground/90">
                          <Icon className="h-3 w-3" /> Sistema
                        </div>
                        {m.message}
                        <div className="text-[10px] mt-1 tabular-nums opacity-80">
                          {new Date(m.created_at).toLocaleString("pt-BR")}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={m.id} className={cn("flex", isOwn ? "justify-end" : "justify-start")}>
                    <div
                      className={cn(
                        "flex gap-2 max-w-[min(92%,420px)]",
                        isOwn ? "flex-row-reverse" : "flex-row"
                      )}
                    >
                      <div
                        className={cn(
                          "h-9 w-9 rounded-full shrink-0 flex items-center justify-center text-[11px] font-semibold border",
                          isOwn
                            ? "bg-[#d7b369]/25 border-[#d7b369]/50 text-[#791614]"
                            : "bg-[#791614]/10 border-[#791614]/25 text-[#791614]"
                        )}
                      >
                        {iniciais(m.sender_name || "?")}
                      </div>
                      <div className="min-w-0 flex flex-col gap-0.5">
                        <div
                          className={cn(
                            "flex flex-wrap items-baseline gap-x-2 gap-y-0 text-[11px]",
                            isOwn ? "justify-end" : "justify-start"
                          )}
                        >
                          <span className="font-medium text-foreground truncate max-w-[200px]">
                            {isOwn ? "Você" : m.sender_name || "Usuário"}
                          </span>
                          <span className="text-muted-foreground tabular-nums">
                            {new Date(m.created_at).toLocaleString("pt-BR")}
                          </span>
                        </div>
                        <div
                          className={cn(
                            "rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words shadow-sm border",
                            isOwn
                              ? "bg-[#d7b369]/10 border-[#d7b369]/35 text-foreground rounded-tr-sm"
                              : "bg-[#791614]/[0.06] border-[rgba(121,22,20,0.12)] text-foreground rounded-tl-sm"
                          )}
                        >
                          {m.subject && m.subject !== thread?.assunto && (
                            <p className="text-xs font-semibold text-[#791614] mb-1">{m.subject}</p>
                          )}
                          {m.message}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} className="h-px w-full shrink-0" aria-hidden />
          </div>
        </ScrollArea>

        {podeConcluir && (
          <div className="shrink-0 border-t border-[rgba(169,171,174,0.35)] bg-[#f9f6f0] p-3 flex justify-center rounded-b-xl">
            <Button
              type="button"
              variant="bordeaux"
              size="sm"
              className="min-w-[240px] gap-2"
              disabled={concluindo || loading}
              onClick={() => void handleConcluir()}
            >
              {concluindo ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Processando…
                </>
              ) : (
                "Concluir thread e gerar resumo"
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ThreadView;
